import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@circle-react/contexts";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import type { VirtualConversationType } from "../types/VirtualMessage";

const TRANSLATION_KEY_MAP = {
  chat: {
    userKey: "ask_question_initial_message",
    botKey: "ask_question_initial_bot_response",
  },
  feedback: {
    userKey: "give_feedback_initial_message",
    botKey: "give_feedback_initial_bot_response",
  },
  report_bug: {
    userKey: "report_bug_initial_message",
    botKey: "report_bug_initial_bot_response",
  },
  copilot_action: {
    userKey: "perform_an_action_initial_message",
    botKey: "perform_an_action_initial_bot_response",
  },
} as const;

export const useVirtualConversation = () => {
  const history = useHistory();
  const { currentCommunityMember } = usePunditUserContext();
  const {
    addVirtualMessage,
    clearVirtualMessages,
    setVirtualConversationType,
  } = useHelpWidgetContext();

  const createVirtualMessage = (
    content: string,
    authorType: "user" | "bot" = "user",
  ) => ({
    id: `virtual-${Date.now()}`,
    content,
    author_name:
      authorType === "user" ? currentCommunityMember?.name || "User" : "Circle",
    author_type: authorType,
    channel: "circle_bot" as const,
    created_at: new Date().toISOString(),
    show_fading_text_effect: authorType === "bot",
  });

  const getInitialMessage = (type: VirtualConversationType): string => {
    const translationKeys = TRANSLATION_KEY_MAP[type] || "";
    return t(`help_widget.chat_bot.templates.${translationKeys.userKey}`);
  };

  const getBotResponse = (type: VirtualConversationType): string => {
    const translationKeys = TRANSLATION_KEY_MAP[type] || "";
    return t(`help_widget.chat_bot.templates.${translationKeys.botKey}`);
  };

  const startVirtualConversation = (type: VirtualConversationType) => {
    clearVirtualMessages();

    const initialMessage = getInitialMessage(type);

    setVirtualConversationType(type);
    addVirtualMessage(createVirtualMessage(initialMessage, "user"));

    history.push("/conversations/virtual");

    setTimeout(() => {
      const botResponse = getBotResponse(type);
      addVirtualMessage(createVirtualMessage(botResponse, "bot"));
    }, 2000);
  };

  return { startVirtualConversation };
};
